import {
  ICompanyMember,
  ICompanyPure,
  ICompany,
  ICompanyJob,
  ICompanyCultureValue,
  ICompanyCultureLevel,
} from '../../common/models/Company'
import { GET_USER_COMPANY } from '../../constants/api'
import { notEmptyTeam } from '../../utils/typeGuards'
import { ICompanyMemberTeam, ICompanyTeam } from './../../common/models/Company'
import { IUser } from './../../common/models/User'
import errorHandler from './errorHandler'
import {
  getCurrentTerm,
  getLevelSkills,
} from '../../common/helpers/company.helper'
import { isAdmin, isSupport } from '../../common/helpers/isLeadOf'

// Handle the request for the user company and process the response
// The response is heavily processed in order to have all data accessible from different points
// For example, the backend returns only one list of members.
// However, you want the corresponding members from that list to be accessible from the "teams" object
export default (user: IUser, token: string) =>
  fetch(GET_USER_COMPANY, {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + token,
    },
  })
    .then(errorHandler)
    .then(
      async (
        clientCompanyRequest: Response
      ): Promise<{ clientCompany: ICompany; user: IUser }> => {
        const clientCompany: ICompanyPure = await clientCompanyRequest.json()
        const adaptedCompany = adaptBackendCompanyToFrontendCompany(
          clientCompany
        )
        return {
          clientCompany: adaptedCompany,
          user: addMemberDetailsToAuthUser(user, adaptedCompany),
        }
      }
    )

export const addMemberDetailsToAuthUser = (
  user: IUser,
  company: ICompany
): IUser => {
  // Client leads are filtered out of `members`, so include them here to keep their own admin access working
  const userAsCompanyMember = [
    ...company.members,
    ...(company.clientLeads || []),
  ].find((member) => member.email === user.email)
  if (!userAsCompanyMember) {
    throw new Error()
  }

  // Identify the member information about the current authentiated user. Its isAdmin
  // means admin access, which support always has; the flag in members is the right
  return {
    ...user,
    asCompanyMember: {
      ...userAsCompanyMember,
      isAdmin: isAdmin(userAsCompanyMember),
    },
  }
}

export const adaptBackendCompanyToFrontendCompany = (
  clientCompany: ICompanyPure
): ICompany => {
  const companyTeams: ICompanyTeam[] = clientCompany.teams.map((team) => {
    return {
      ...team,
      members: [], // Initially set to empty array. Later, this will be modified
    }
  })

  clientCompany.files =
    clientCompany.files && clientCompany.files.length > 0
      ? clientCompany.files.map((file) => ({
          ...file,
          dateCreated: new Date(file.dateCreated),
        }))
      : undefined

  // Sort members alphabetically
  clientCompany.members = clientCompany.members.sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  const companyJobs: ICompanyJob[] = clientCompany.jobs.map((j) => {
    const department = clientCompany.departments?.find((d) =>
      d.jobsIds.find((dj) => dj + '' === j._id + '')
    )
    // An id that no longer resolves is dropped rather than thrown on: this runs
    // for every level of every job, so one drifted id used to take the whole
    // company load — and with it the app — down. Soft-deleted records drop out
    // too, so `level.skills` says the same thing as `company.skills` below.
    const jobLevelsWithSkills = j.levels.map((jl) => ({
      ...jl,
      skills: getLevelSkills(jl, clientCompany),
    }))
    return {
      ...j,
      levels: jobLevelsWithSkills.sort((a, b) => a.rank - b.rank),
      department,
    }
  })

  // For each member, find the teams to which he/she is linked
  const companyMembers: ICompanyMember[] = clientCompany.members.map(
    (member) => {
      const memberTeams: ICompanyMemberTeam[] = member.teams
        .map((team) => {
          const correspondingTeam = companyTeams.find(
            (companyTeam) => companyTeam._id + '' === team.teamId + ''
          )

          return {
            ...team,
            team: correspondingTeam,
          }
        })
        .filter(notEmptyTeam)

      const memberJob = companyJobs.find(
        (jobFull) => jobFull._id + '' === member.job?.jobId + ''
      )
      if (!memberJob) {
        return {
          ...member,
          teams: memberTeams,
        }
      }
      const memberLevel = memberJob.levels.find(
        (levelFull) => levelFull._id + '' === member.job?.jobLevelId + ''
      )

      // Sort levels by rank
      memberJob.levels = memberJob.levels.sort((a, b) => a.rank - b.rank)
      return {
        ...member,
        teams: memberTeams,
        jobFull: memberJob,
        jobLevel: memberLevel,
      }
    }
  )

  // For each team, find the members that are part of that team, and store them in the object
  companyTeams.forEach((team) => {
    team.members = companyMembers.filter(
      (e) =>
        !e.isDisabled &&
        !isSupport(e) &&
        e.teams.find((memberTeam) => memberTeam.team._id + '' === team._id + '')
    )
  })

  const companySkills = clientCompany.skills

  const cultureValues: ICompanyCultureValue[] = clientCompany.cultureValues.map(
    (cv) => {
      const cultureLevels: ICompanyCultureLevel[] = cv.levels.map((cl) => {
        const cultureRank = clientCompany.cultureRanks.find(
          (cr) => String(cr._id) === String(cl.cultureRankId)
        )
        if (!cultureRank) {
          throw new Error(
            `Culture rank not found. Company ${clientCompany._id}. Culture level ${cl._id}`
          )
        }
        return { ...cl, rank: cultureRank?.rank, title: cultureRank?.title }
      })
      return { ...cv, levels: cultureLevels }
    }
  )

  const companyDepartments = clientCompany.departments?.map((dep) => ({
    ...dep,
    jobs: companyJobs.filter((job) => job.department?._id === dep._id),
  }))

  return {
    ...clientCompany,
    allCultureValues: cultureValues,
    cultureValues: cultureValues.filter((value) => !value.inactive),
    teams: companyTeams.sort((a, b) => a.name.localeCompare(b.name)),
    // IMPROVEMENT: This can be greatly improved by using a hashmap
    members: companyMembers.filter(
      (member) => !member.isDisabled && !isSupport(member)
    ),
    disabledMembers: companyMembers.filter(
      (member) => member.isDisabled && !isSupport(member)
    ),
    allMembers: companyMembers.filter((member) => !isSupport(member)),
    clientLeads: companyMembers.filter((m) => isSupport(m) && !m.isDisabled),
    skills: companySkills.filter((s) => !s.inactive),
    allSkills: companySkills,
    teamLeadSkills: companySkills.filter((skill) => skill.teamLead),
    currentTerm:
      clientCompany.termsInformation && getCurrentTerm(clientCompany),
    jobs: companyJobs,
    departments: companyDepartments,
  }
}
